import { tz } from "@date-fns/tz";
import { format } from "date-fns";
import type { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import type { FormMode } from "@/components/admin-portal/appointment/new-appointment-form";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import type {
	Appointment,
	AppointmentPayload,
} from "@/types/admin-portal/appointment";
import type { Auth } from "@/types/globals";
import {
	DEFAULT_TIMEZONE,
	FISIOHOME_PARTNER,
	GENDERS,
	PATIENT_CONDITIONS,
	PATIENT_REFERRAL_OPTIONS,
	PREFERRED_THERAPIST_GENDER,
} from "../constants";
import { isValidPhone, PHONE_FORMAT_MESSAGE } from "../phone";
import { boolSchema, idSchema } from "../validation";

export const DEFAULT_VALUES_LOCATION = {
	id: "",
	city: "",
} satisfies AppointmentBookingSchema["patientDetails"]["location"];
export const DEFAULT_VALUES_SERVICE = {
	id: "",
	name: "",
} satisfies AppointmentBookingSchema["appointmentScheduling"]["service"];
export const DEFAULT_VALUES_PACKAGE = {
	id: "",
	name: "",
	numberOfVisit: 0,
} satisfies AppointmentBookingSchema["appointmentScheduling"]["package"];
export const DEFAULT_VALUES_THERAPIST = {
	id: "",
	name: "",
} satisfies NonNullable<
	AppointmentBookingSchema["appointmentScheduling"]["therapist"]
>;
export const DEFAULT_VALUES_PATIENT_ADDRESS = {
	address: "",
	postalCode: "",
	latitude: 0,
	longitude: 0,
	addressNotes: undefined,
} satisfies Pick<
	AppointmentBookingSchema["patientDetails"],
	"latitude" | "longitude" | "postalCode" | "addressNotes" | "address"
>;
export const DEFAULT_VALUES_PATIENT_CONTACT = {
	contactName: "",
	contactPhone: "",
	email: undefined,
	miitelLink: undefined,
} satisfies AppointmentBookingSchema["contactInformation"];

/**
 * * new appointment booking form schema
 */

// * contact information schema
export const CONTACT_INFORMATION_SCHEMA = z.object({
	contactName: z.string().min(1, "Contact name is required"),
	contactPhone: z
		.string()
		.min(1, { message: "Contact phone number is required" }),
	email: z.string().email("Invalid email").or(z.literal("")).optional(),
	miitelLink: z.string().url("MiiTel link must be a valid URL").optional(),
});
export type ContactInformationSchema = z.infer<
	typeof CONTACT_INFORMATION_SCHEMA
>;

// * patient details schema
export const PATIENT_DETAILS_SCHEMA = z.object({
	fullName: z.string().min(1, "Patient full name is required"),
	dateOfBirth: z.coerce
		.date()
		// Ensure the date is in the past
		.refine((date) => date < new Date(), {
			message: "Date of birth must be in the past",
		}),
	age: z.coerce.number().min(0),
	gender: z.enum(GENDERS, {
		required_error: "Need to select a patient gender",
	}),
	illnessOnsetDate: z.string().optional(),
	complaintDescription: z.string().min(1, "Complaint description is required"),
	condition: z.enum(PATIENT_CONDITIONS, {
		required_error: "Need to select a patient condition",
	}),
	medicalHistory: z.string().optional(),
	location: z
		.object({
			id: idSchema,
			city: z.string(),
		})
		.refine((value) => !!value?.city, {
			path: ["city"],
			message: "Please select a location",
		}),
	postalCode: z.string().optional(),
	latitude: z.coerce
		.number({ message: "Latitude must be a numerical value" })
		.refine((value) => value !== 0, {
			message: "Latitude is required",
		}),
	longitude: z.coerce
		.number({ message: "Longitude must be a numerical value" })
		.refine((value) => value !== 0, {
			message: "Longitude is required",
		}),
	address: z.string().min(1, "Address is required"),
	addressNotes: z.string().optional(),
});
export type PatientDetailsSchema = z.infer<typeof PATIENT_DETAILS_SCHEMA>;

// The visit-location half of PATIENT_DETAILS_SCHEMA. The booking form asks for
// these on the scheduling step while the rest is asked on the patient step, so
// anything that reasons about "which step does this field belong to" — step
// validation, the patient-selection alert, whether to render the location form —
// splits the namespace here. Named once, as a schema: the keys and the
// completeness check are both derived from it so they cannot drift apart.
export const PATIENT_LOCATION_SCHEMA = PATIENT_DETAILS_SCHEMA.pick({
	location: true,
	postalCode: true,
	address: true,
	addressNotes: true,
	latitude: true,
	longitude: true,
});

export const PATIENT_LOCATION_KEYS = Object.keys(
	PATIENT_LOCATION_SCHEMA.shape,
) as (keyof PatientDetailsSchema)[];

export const isPatientLocationKey = (key: string): boolean =>
	(PATIENT_LOCATION_KEYS as string[]).includes(key);

// Who the patient is: what picking an existing patient fills in. An error here means
// no patient was picked. The medical record (complaint, condition) is not part of it —
// the admin fills that per Booking even for a patient already on record.
export const PATIENT_IDENTITY_KEYS: (keyof PatientDetailsSchema)[] = [
	"fullName",
	"dateOfBirth",
	"age",
	"gender",
];

export const isPatientIdentityKey = (key: string): boolean =>
	(PATIENT_IDENTITY_KEYS as string[]).includes(key);

/** Whether a patient's stored address is complete enough to schedule a visit against. */
export const hasCompleteVisitLocation = (patientDetails: unknown): boolean =>
	PATIENT_LOCATION_SCHEMA.safeParse(patientDetails).success;

// Common therapist schema
const THERAPIST_SCHEMA = z
	.object({
		id: idSchema.optional(),
		name: z.string().optional(),
		// Employment type of the chosen therapist (KARPIS=internal, FLAT=external).
		// Persisted so the schedule picker can default the type filter to the
		// continuity therapist on later visits. Ignored by the booking payload.
		employmentType: z.enum(["KARPIS", "FLAT"]).optional(),
		// Display-only fields shown on the "selected therapist" card. Persisted so
		// they survive a draft round-trip: without them here, zodResolver strips the
		// keys whenever validated values are saved (e.g. "Save & Next"), and the card
		// loses reg number / gender after a refresh. Ignored by the booking payload.
		registrationNumber: z.string().optional(),
		gender: z.string().optional(),
	})
	.optional()
	.nullable();

/**
 * Coerce an arbitrary value to a valid Date or null. Accepts Date objects and
 * date strings (e.g. ISO strings restored from a draft/session); returns null for
 * empty/undefined/unparseable inputs and for Invalid Date objects.
 *
 * Use before date-fns `format()` / `Date#toISOString()` to avoid the
 * "Invalid time value" RangeError they throw on invalid dates, and as the
 * appointment date-time schema's normalizer.
 */
export const toValidDate = (value: unknown): Date | null => {
	if (value === "" || value === null || value === undefined) return null;
	const date = value instanceof Date ? value : new Date(value as string);
	return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Whether a Visit's date carries a time someone chose. The calendar hands back a bare
 * day at 00:00 WIB and no home visit starts then, so midnight is how this form spells
 * "date picked, time not yet" — and a Visit saved that way would be booked at midnight.
 */
export const hasVisitTime = (value: Date | null | undefined): boolean =>
	!!value && format(value, "HH:mm", { in: tz(DEFAULT_TIMEZONE) }) !== "00:00";

export const VISIT_TIME_REQUIRED_ERROR = "Pick the visit time";

// Common appointment date time schema.
// Normalize "no usable date" inputs (undefined, "", an Invalid Date object, or an
// unparseable string) to null instead of erroring. A missing date is the valid
// "Unscheduled" state, but the previous `z.coerce.date().nullable()` only accepted
// `null`: stale field-array / restored-draft / session state that carried undefined
// or "" surfaced a misleading "Invalid date" toast on "Save & Next" (intermittent,
// series bookings especially). Mirrors the visitNumber repair in series-scheduler.tsx.
const APPOINTMENT_DATE_TIME_SCHEMA = z.preprocess(
	(value) => toValidDate(value),
	z.date().nullable(),
);

// Base appointment fields that are shared
const BASE_APPOINTMENT_FIELDS = {
	preferredTherapistGender: z.enum(PREFERRED_THERAPIST_GENDER, {
		required_error: "Please select a preferred therapist gender",
	}),
	therapist: THERAPIST_SCHEMA,
	findTherapistsAllOfDay: z.boolean().optional(),
};

// * series visit schema (without preferredTherapistGender - it's set at appointment level)
export const SERIES_VISIT_SCHEMA = z.object({
	therapist: THERAPIST_SCHEMA,
	findTherapistsAllOfDay: z.boolean().optional(),
	visitNumber: z.number().int().positive(),
	appointmentDateTime: APPOINTMENT_DATE_TIME_SCHEMA.nullable(),
	mitraAssignmentReason: z.string().nullish(),
});

export type SeriesVisitSchema = z.infer<typeof SERIES_VISIT_SCHEMA>;

// * appointment scheduling schema
export const APPOINTMENT_SCHEDULING_SCHEMA = z.object({
	service: z
		.object({
			id: idSchema,
			name: z.string(),
		})
		.refine((value) => !!value?.name, {
			path: ["name"],
			message: "Please select a service",
		}),
	package: z
		.object({
			id: idSchema,
			name: z.string(),
			numberOfVisit: z.coerce.number().int().positive(),
		})
		.refine((value) => !!value?.name, {
			path: ["name"],
			message: "Please select a package",
		}),
	...BASE_APPOINTMENT_FIELDS,
	appointmentDateTime: APPOINTMENT_DATE_TIME_SCHEMA,
	// Add series visits array for multiple appointments
	seriesVisits: z.array(SERIES_VISIT_SCHEMA).optional(),
	// Add flag to indicate if series scheduling is enabled
	enableSeriesScheduling: z.boolean().default(false),
	mitraAssignmentReason: z.string().nullish(),
});

type TherapistRef = {
	id?: string | number | null;
	employmentType?: string;
} | null;

type VisitFields = {
	therapist?: TherapistRef;
	mitraAssignmentReason?: string | null;
};
type SchedulingVisitFields = VisitFields & {
	seriesVisits?: (VisitFields | null | undefined)[];
};

export const visitsOf = (scheduling: SchedulingVisitFields | undefined) => {
	const series = Array.isArray(scheduling?.seriesVisits)
		? scheduling.seriesVisits
		: [];

	return [
		{
			therapist: scheduling?.therapist,
			mitraAssignmentReason: scheduling?.mitraAssignmentReason,
			reasonPath: ["mitraAssignmentReason"] as (string | number)[],
		},
		...series.map((visit, index) => ({
			therapist: visit?.therapist,
			mitraAssignmentReason: visit?.mitraAssignmentReason,
			reasonPath: ["seriesVisits", index, "mitraAssignmentReason"] as (
				| string
				| number
			)[],
		})),
	];
};

export const mitraReasonFieldName = (index: number) =>
	(index === 0
		? "appointmentScheduling.mitraAssignmentReason"
		: `appointmentScheduling.seriesVisits.${index - 1}.mitraAssignmentReason`) as
		| "appointmentScheduling.mitraAssignmentReason"
		| `appointmentScheduling.seriesVisits.${number}.mitraAssignmentReason`;

/** The visit index whose reason field `path` names, the inverse of mitraReasonFieldName. */
export const mitraReasonVisitOf = (path: string) => {
	if (path === mitraReasonFieldName(0)) return 0;
	const series =
		/^appointmentScheduling\.seriesVisits\.(\d+)\.mitraAssignmentReason$/.exec(
			path,
		);
	return series ? Number(series[1]) + 1 : undefined;
};

export const isMitra = (therapist: TherapistRef | undefined) =>
	therapist?.employmentType === "FLAT";

// Blank ids count as nobody: Rails writes the therapist as null,
// fisiohome-backend writes {"id": "", "name": ""}.
const therapistIdOf = (therapist: TherapistRef | undefined) =>
	String(therapist?.id ?? "").trim();

/** Mirrors MitraAssignmentReasonService.owed? on the server; change both together. */
export const owesMitraReason = (
	from: TherapistRef | undefined,
	to: TherapistRef | undefined,
) => isMitra(to) && therapistIdOf(to) !== therapistIdOf(from);

/**
 * Message keys, not copy. This module is a plain constant evaluated at import time,
 * so it has no `t()` and cannot import the i18n instance either — that module reads
 * `window` on load, which would break this schema's own unit tests under Node. The
 * field component translates these under
 * `appt_schedule.fields.mitra_assignment_reason.errors`.
 */
export const MITRA_ASSIGNMENT_REASON_ERRORS = {
	required: "required",
	notApplicable: "not_applicable",
} as const;

/** A Customer-Created Draft's Draft Baseline, kept by the server (docs/adr/0013). */
export type DraftBaseline = {
	therapistIds?: (string | null)[];
	mitraAssignmentReasons?: (string | null | undefined)[];
};

/** The baseline `scheduling` would produce; mirrors AppointmentDraft.baseline_of. */
export const draftBaselineOf = (
	scheduling: SchedulingVisitFields | undefined,
): DraftBaseline => {
	const visits = visitsOf(scheduling);
	return {
		therapistIds: visits.map((visit) => therapistIdOf(visit.therapist)),
		mitraAssignmentReasons: visits.map((visit) => visit.mitraAssignmentReason),
	};
};

export const mitraReasonOwed = (
	scheduling: SchedulingVisitFields | undefined,
	index: number,
	baseline: DraftBaseline = {},
) =>
	owesMitraReason(
		baseline.therapistIds ? { id: baseline.therapistIds[index] } : undefined,
		visitsOf(scheduling)[index]?.therapist,
	);

export const mitraReasonFieldShown = (
	scheduling: SchedulingVisitFields | undefined,
	index: number,
	baseline: DraftBaseline = {},
) =>
	isMitra(visitsOf(scheduling)[index]?.therapist) &&
	(mitraReasonOwed(scheduling, index, baseline) ||
		Boolean(baseline.mitraAssignmentReasons?.[index]));

/** The visits the form's single reason field writes to, in visit order. */
export const mitraReasonVisits = (
	scheduling: SchedulingVisitFields | undefined,
	baseline: DraftBaseline = {},
) =>
	visitsOf(scheduling).flatMap((visit, index) =>
		mitraReasonFieldShown(scheduling, index, baseline)
			? [{ ...visit, index }]
			: [],
	);

/**
 * Stored per visit, asked once per Booking (decided 19 Oct 2026): every visit the field
 * covers carries the same reason, and a visit that leaves the field loses its own.
 */
export const mitraReasonUpdates = (
	scheduling: SchedulingVisitFields | undefined,
	baseline: DraftBaseline = {},
) => {
	const covered = mitraReasonVisits(scheduling, baseline);
	const reason = covered.find(
		(visit) => visit.mitraAssignmentReason,
	)?.mitraAssignmentReason;
	const coveredIndexes = new Set(covered.map((visit) => visit.index));
	const updates: [number, string | null][] = [];

	visitsOf(scheduling).forEach((visit, index) => {
		if (!coveredIndexes.has(index)) {
			if (visit.mitraAssignmentReason) updates.push([index, null]);
		} else if (reason && visit.mitraAssignmentReason !== reason) {
			updates.push([index, reason]);
		}
	});

	return updates;
};

/**
 * The rule hangs here rather than on APPOINTMENT_BOOKING_SCHEMA (where the
 * seriesVisits count rule lives) because a root-level refine only runs once every
 * step parses — so an unrelated error on the patient step would silently skip it.
 */
export const buildSchedulingStepSchema = (
	baseline: DraftBaseline = {},
	enabled = true,
) =>
	APPOINTMENT_SCHEDULING_SCHEMA.superRefine((scheduling, ctx) => {
		const visitDates: [Date | null | undefined, (string | number)[]][] = [
			[scheduling.appointmentDateTime, ["appointmentDateTime"]],
			...(scheduling.seriesVisits ?? []).map(
				(visit, index) =>
					[
						visit.appointmentDateTime,
						["seriesVisits", index, "appointmentDateTime"],
					] as [Date | null | undefined, (string | number)[]],
			),
		];
		for (const [date, path] of visitDates) {
			if (date && !hasVisitTime(date)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path,
					message: VISIT_TIME_REQUIRED_ERROR,
				});
			}
		}

		if (!enabled) return;

		visitsOf(scheduling).forEach((visit, index) => {
			if (
				!visit.mitraAssignmentReason &&
				mitraReasonOwed(scheduling, index, baseline)
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: visit.reasonPath,
					message: MITRA_ASSIGNMENT_REASON_ERRORS.required,
				});
			}
			if (visit.mitraAssignmentReason && !isMitra(visit.therapist)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: visit.reasonPath,
					message: MITRA_ASSIGNMENT_REASON_ERRORS.notApplicable,
				});
			}
		});
	});

/** The rule as it applies to a Booking with no history — every assignment is the admin's. */
export const APPOINTMENT_SCHEDULING_STEP_SCHEMA = buildSchedulingStepSchema();

export type AppointmentSchedulingSchema = z.infer<
	typeof APPOINTMENT_SCHEDULING_SCHEMA
>;

/**
 * Checks if the given value is a custom referral.
 *
 * This function determines whether the provided value is not included in the predefined
 * list of patient referral options (`PATIENT_REFERRAL_OPTIONS`). If the value is not found
 * in the list, it is considered a custom referral.
 *
 * @param value - The referral value to check.
 * @returns `true` if the value is a custom referral, `false` otherwise.
 */
export const checkIsCustomReferral = (value: string) => {
	const options = PATIENT_REFERRAL_OPTIONS.filter(
		(i) => i.value !== "OTHER",
	).map((i) => i.value) as string[];

	return !!value && !options.includes(value);
};

/**
 * Checks if the given value is not included in the PATIENT_REFERRAL_OPTIONS array.
 *
 * @param value - The string value to check against the PATIENT_REFERRAL_OPTIONS array.
 * @returns A boolean indicating whether the value is not a custom Fisiohome partner.
 */
export const checkIsCustomFisiohomePartner = (value: string) => {
	const options = FISIOHOME_PARTNER.filter((i) => i.value !== "OTHER").map(
		(i) => i.value,
	) as string[];

	return value && !options.includes(value);
};

// additional settings schema
export const ADDITIONAL_SETTINGS_SCHEMA = z
	.object({
		referralSource: z.string().optional(),
		customReferralSource: z.string().optional(),
		fisiohomePartnerBooking: boolSchema.default(false),
		fisiohomePartnerName: z.string().optional(),
		customFisiohomePartnerName: z.string().optional(),
		voucherCode: z.string().optional(),
		notes: z.string().optional(),
		bypassSlotConflict: boolSchema.default(false),
		enablePaymentGateway: boolSchema.default(false),
		// nullish, not optional: a restored draft / sessionStorage payload sends an
		// explicit null for an unset expiry, and the field is hidden while the
		// gateway is off — so a type error here blocks the step invisibly.
		paymentExpiryMinutes: z.string().nullish(),
		admins: z
			.array(z.object({ id: idSchema, name: z.string(), email: z.string() }))
			.optional(),
		// The three KPI slots — who made the Draft, who carries it on, who closes it as an
		// Order. Bare admin ids: the page already has every admin's name to render them
		// with, and the server serves these back from its own columns on every load.
		//
		// nullish for the same reason paymentExpiryMinutes is: a restored draft sends an
		// explicit null for an empty slot, and a plain .optional() would reject it — an
		// invisible block on the step. The "no L2/L3 without L1" rule is enforced on the
		// model, which is the one place all four write paths pass through.
		picL1: z.string().nullish(),
		picL2: z.string().nullish(),
		picL3: z.string().nullish(),
	})
	.superRefine((data, ctx) => {
		// Only perform the check if booking from partner is selected.
		if (data.fisiohomePartnerBooking && !data.fisiohomePartnerName) {
			// If booking is not selected, no need to check the name.
			// Otherwise, check that the partner name exists and is not just whitespace.
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message:
					"Please select the fisiohome partner's name to complete the partner booking",
				path: ["fisiohomePartnerName"],
			});
		}

		// checking if the custom referral source filled correctly
		if (
			checkIsCustomReferral(data.referralSource || "") &&
			!data.customReferralSource
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message:
					"Please input the patient referral source to complete the partner booking",
				path: ["customReferralSource"],
			});
		}

		// checking if the custom fisiohome partner filled correctly
		if (
			checkIsCustomFisiohomePartner(data.fisiohomePartnerName || "") &&
			!data.customFisiohomePartnerName
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message:
					"Please input the fisiohome partner to complete the partner booking.",
				path: ["customFisiohomePartnerName"],
			});
		}

		// checking if payment expiry is selected when payment gateway is enabled
		if (data.enablePaymentGateway && !data.paymentExpiryMinutes) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Please select a payment expiration time.",
				path: ["paymentExpiryMinutes"],
			});
		}
	});
export type AdditionalSettingsSchema = z.infer<
	typeof ADDITIONAL_SETTINGS_SCHEMA
>;

/**
 * Wipe the payment expiry when the gateway is switched off.
 *
 * The expiry field renders only while the gateway is on, so an error left on it
 * is invisible to the admin while still failing the Additional Settings step,
 * and a stale value would still ride along in buildAppointmentPayload's
 * rest-spread. Value and error therefore have to go together.
 */
export const clearPaymentExpiry = (
	form: Pick<
		UseFormReturn<AppointmentBookingSchema>,
		"setValue" | "clearErrors"
	>,
) => {
	form.setValue("additionalSettings.paymentExpiryMinutes", undefined);
	form.clearErrors("additionalSettings.paymentExpiryMinutes");
};
// Admin PIC schema for draft feature
export const ADMIN_PIC_SCHEMA = z.object({
	id: idSchema,
	name: z.string(),
	email: z.string().optional(),
});
export type AdminPicSchema = z.infer<typeof ADMIN_PIC_SCHEMA>;

export const ADMINS_SCHEMA = z
	.array(ADMIN_PIC_SCHEMA)
	.min(1, "At least one admin PIC is required");
export type AdminsSchema = z.infer<typeof ADMINS_SCHEMA>;

// form options schema
//
// The three sources carry defaults rather than being plain required enums. Nothing on
// screen is bound to them as a required question — they mirror UI toggles that always
// have a selection — but they are also rendered by no step, so a draft that arrives
// without them (fisiohome-backend and the WA paste flow both write form_data of their
// own) failed the booking schema at submit time with no field anywhere for the admin to
// fix. The defaults match defineAppointmentFormDefaultValues.
export const FORM_OPTIONS_SCHEMA = z.object({
	patientRecordSource: z.enum(["existing", "add"]).default("existing"),
	patientContactSource: z.enum(["existing", "new"]).default("existing"),
	coordinateSource: z.enum(["manual", "automatic"]).default("automatic"),
	referenceAppointmentId: idSchema.optional(),
	findTherapistsAllOfDay: z.boolean().optional(),
	// The picked existing patient, for the link to edit their contact. Not sent on booking.
	patientId: z.string().optional(),
	// Draft-related fields
	draftId: idSchema.optional(),
	queueCode: z.string().optional(),
});
export type FormOptionsSchema = z.infer<typeof FORM_OPTIONS_SCHEMA>;

/**
 * all merge schema
 *
 * Takes the DraftBaseline so it can reach the scheduling step's Mitra rule — see
 * buildSchedulingStepSchema. Pass what a continued Draft loaded with; omit it for a
 * Booking started here. `mitraReasonEnabled` carries the MITRA_ASSIGNMENT_REASON flag
 * down to the same rule.
 */
export const buildBookingSchema = (
	baseline: DraftBaseline = {},
	mitraReasonEnabled = true,
) =>
	z.preprocess(
		(data, ctx) => {
			// A preprocess, not a root refine: Zod skips a root refine while any later
			// step is still empty, so step 1 would never see this. An existing patient's
			// contact is not written by the server (#306), so a broken stored number
			// must not block the booking; it is fixed in Edit Kontak Pasien.
			const values = data as Partial<AppointmentBookingSchema> | undefined;
			const phone = values?.contactInformation?.contactPhone;
			if (
				values?.formOptions?.patientRecordSource !== "existing" &&
				phone &&
				!isValidPhone(phone)
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: PHONE_FORMAT_MESSAGE,
					path: ["contactInformation", "contactPhone"],
				});
			}
			return data;
		},
		z
			.object({
				// Defaulted as a whole, not just field by field: a draft written elsewhere
				// can omit the namespace outright, and a required object would then fail the
				// submit over something no step renders.
				formOptions: FORM_OPTIONS_SCHEMA.default({}),
				contactInformation: CONTACT_INFORMATION_SCHEMA,
				patientDetails: PATIENT_DETAILS_SCHEMA,
				appointmentScheduling: buildSchedulingStepSchema(
					baseline,
					mitraReasonEnabled,
				),
				additionalSettings: ADDITIONAL_SETTINGS_SCHEMA,
			})
			.refine(
				(data) => {
					const {
						package: pkg,
						enableSeriesScheduling,
						seriesVisits = [],
					} = data.appointmentScheduling;

					// If series scheduling is not enabled or no package is selected, validation passes
					if (!enableSeriesScheduling || !pkg) return true;

					// If series scheduling is enabled, we need to have the correct number of visits
					return seriesVisits.length === pkg.numberOfVisit - 1; // -1 because the first visit is the main appointment
				},
				{
					message:
						"Number of series visits must match the package's number of visits minus one",
					path: ["appointmentScheduling.seriesVisits"],
				},
			),
	);

export const APPOINTMENT_BOOKING_SCHEMA = buildBookingSchema();
export type AppointmentBookingSchema = z.infer<
	typeof APPOINTMENT_BOOKING_SCHEMA
>;

// for prepare the payload appointment form
export const buildAppointmentPayload = (values: AppointmentBookingSchema) => {
	const {
		contactInformation,
		additionalSettings,
		appointmentScheduling,
		patientDetails,
		formOptions,
	} = values;
	const { referenceAppointmentId, draftId, queueCode } = formOptions;
	const {
		admins,
		customFisiohomePartnerName,
		customReferralSource,
		picL1: _picL1,
		picL2: _picL2,
		picL3: _picL3,
		...restAdditionalSettings
	} = additionalSettings;
	const {
		package: appointmentPackage,
		service,
		therapist,
		seriesVisits,
		enableSeriesScheduling: _enableSeriesScheduling,
		...restAppointmentScheduling
	} = appointmentScheduling;
	const {
		location,
		illnessOnsetDate,
		complaintDescription,
		condition,
		medicalHistory,
		fullName,
		age: _age,
		dateOfBirth,
		gender,
		addressNotes,
		...restPatientDetails
	} = patientDetails;

	const payload = deepTransformKeysToSnakeCase({
		serviceId: String(service.id),
		packageId: String(appointmentPackage.id),
		locationId: String(location.id),
		therapistId: String(therapist?.id || "") || null,
		adminIds: admins?.map((admin) => String(admin.id)).join(",") || "",
		referenceAppointmentId: String(referenceAppointmentId || "") || null,
		draftId: String(draftId || "") || null,
		queueCode: String(queueCode || "") || null,
		patientContact: { ...contactInformation },
		patientAddress: {
			...restPatientDetails,
			postalCode: restPatientDetails?.postalCode || "",
			locationId: String(location.id),
			notes: addressNotes,
		},
		patient: {
			name: fullName,
			dateOfBirth,
			// dateOfBirth: add(dateOfBirth, { hours: 7 }),
			gender,
		},
		appointment: {
			...restAdditionalSettings,
			...restAppointmentScheduling,
			otherReferralSource: customReferralSource,
			otherFisiohomePartnerName: customFisiohomePartnerName,
			patientIllnessOnsetDate: illnessOnsetDate,
			patientComplaintDescription: complaintDescription,
			patientCondition: condition,
			patientMedicalHistory: medicalHistory,
			seriesVisits: seriesVisits?.length
				? seriesVisits.map(
						({ findTherapistsAllOfDay, therapist, ...rest }) => ({
							...rest,
							therapistId: String(therapist?.id || "") || null,
							appointmentDateTime: rest?.appointmentDateTime || null,
						}),
					)
				: undefined,
		},
	} satisfies AppointmentPayload);

	return payload;
};

// define the form default values
interface FormDefaultProps {
	mode?: FormMode;
	admin?: Auth["currentUser"];
	apptRef?: Appointment | null;
}

// @ts-ignore
const _devValues = () => {
	// for referral
	const referralSource = "OTHER";
	const isCustomReferral = checkIsCustomReferral(referralSource);
	const customReferralSource = isCustomReferral ? "Linkedin" : undefined;
	// for fisiohome partner name
	const fisiohomePartnerName = "OTHER";
	const isCustomFisiohomePartner =
		checkIsCustomFisiohomePartner(fisiohomePartnerName);
	const customFisiohomePartnerName = isCustomFisiohomePartner
		? "Tokopedia"
		: undefined;

	return {
		contactInformation: {
			contactName: "Dendy",
			contactPhone: "+62896272346",
			email: "dendy@yopmail.com",
		},
		patientDetails: {
			fullName: "Dendy Dandees",
			dateOfBirth: null,
			age: null,
			gender: "MALE",
			condition: "NORMAL",
			medicalHistory: "Hipertensi",
			complaintDescription: "Sakit pinggang",
			illnessOnsetDate: "2 minggu lalu",
			addressNotes: "Taman suropati",
			address:
				"Jl. Taman Suropati No.5, RT.5/RW.5, Menteng, Kec. Menteng, Kota Jakarta Pusat, Daerah Khusus Ibukota Jakarta 10310",
			postalCode: "10310",
			latitude: -6.19841,
			longitude: 106.83209,
			location: {
				id: "5",
				city: "KOTA ADM. JAKARTA PUSAT",
			},
			// address:
			// 	"Wang Plaza, Jl. Panjang No.kav 17, RT.14/RW.7, Kedoya Utara, Kec. Kb. Jeruk, jakarta, Daerah Khusus Ibukota Jakarta 11520",
			// postalCode: "11520",
			// latitude: -6.17381,
			// longitude: 106.76558,
			// location: {
			// 	id: "2",
			// 	city: "KOTA ADM. JAKARTA BARAT",
			// },
		},
		appointmentScheduling: {
			// service: { id: "1", name: "FISIOHOME_SPECIAL_TIER" },
			// package: { id: "3", name: "Paket Suite", numberOfVisit: 6 },
			service: { id: "", name: "" },
			package: { id: "", name: "", numberOfVisit: 0 },
			preferredTherapistGender: "NO PREFERENCE",
			appointmentDateTime: null,
		},
		additionalSettings: {
			referralSource,
			customReferralSource,
			fisiohomePartnerBooking: true,
			fisiohomePartnerName,
			customFisiohomePartnerName,
			voucherCode: "TEBUSMURAH",
			notes: "This is the patient notes",
		},
	};
};

export const defineAppointmentFormDefaultValues = (
	props?: FormDefaultProps,
) => {
	// const { contactInformation } = devValues();

	// for the form options
	const patientRecordSource = "existing";
	const patientContactSource = "existing";
	const coordinateSource = "automatic";
	const findTherapistsAllOfDay = false;

	// for appointment references (needed for create a appointment series)
	const referenceAppointmentId = props?.apptRef?.id;

	// for patient details
	const contactInformation = {
		contactName: props?.apptRef?.patient?.contact?.contactName || "",
		contactPhone: props?.apptRef?.patient?.contact?.contactPhone || "",
		email: props?.apptRef?.patient?.contact?.email || "",
	};
	const patientDetails = {
		fullName: props?.apptRef?.patient?.name || "",
		dateOfBirth: props?.apptRef?.patient?.dateOfBirth
			? new Date(props?.apptRef?.patient?.dateOfBirth)
			: null,
		age: props?.apptRef?.patient?.age || null,
		gender: props?.apptRef?.patient?.gender || "MALE",
		condition: "NORMAL",
		medicalHistory: "",
		complaintDescription: "",
		illnessOnsetDate: "",
		addressNotes: props?.apptRef?.visitAddress?.notes || "",
		address: props?.apptRef?.visitAddress?.addressLine || "",
		postalCode: props?.apptRef?.visitAddress?.postalCode || "",
		latitude: props?.apptRef?.visitAddress?.latitude || 0,
		longitude: props?.apptRef?.visitAddress?.longitude || 0,
		location: {
			id: props?.apptRef?.location?.id || "",
			city: props?.apptRef?.location?.city || "",
		},
	};

	// for appointment details
	const appointmentScheduling = {
		service: {
			id: props?.apptRef?.service?.id || "",
			name: props?.apptRef?.service?.name || "",
		},
		package: {
			id: props?.apptRef?.package?.id || "",
			name: props?.apptRef?.package?.name || "",
			numberOfVisit: props?.apptRef?.package?.numberOfVisit || 0,
		},
		preferredTherapistGender:
			props?.apptRef?.preferredTherapistGender || "NO PREFERENCE",
		appointmentDateTime: null,
	};

	// for additional settings
	const admins = props?.admin?.id
		? [
				{
					id: props.admin.id,
					name: props.admin.name || "",
					email: props.admin.user?.email || "",
				},
			]
		: [];
	const referralSource = props?.apptRef?.otherReferralSource
		? "OTHER"
		: props?.apptRef?.referralSource || undefined;
	const customReferralSource =
		referralSource && checkIsCustomReferral(referralSource)
			? props?.apptRef?.otherReferralSource
			: undefined;
	const fisiohomePartnerBooking = !!props?.apptRef?.fisiohomePartnerBooking;
	const fisiohomePartnerName = props?.apptRef?.otherFisiohomePartnerName
		? "OTHER"
		: props?.apptRef?.fisiohomePartnerName || undefined;
	const customFisiohomePartnerName =
		fisiohomePartnerName && checkIsCustomFisiohomePartner(fisiohomePartnerName)
			? props?.apptRef?.otherFisiohomePartnerName
			: undefined;
	const additionalSettings = {
		referralSource,
		customReferralSource,
		fisiohomePartnerBooking,
		fisiohomePartnerName,
		customFisiohomePartnerName,
		voucherCode: props?.apptRef?.voucherCode || undefined,
		notes: props?.apptRef?.notes || undefined,
		bypassSlotConflict: false,
		enablePaymentGateway: false,
		paymentExpiryMinutes: undefined,
		admins,
		// L1 defaults to whoever is filling the form in, exactly as the PIC multi-select
		// above already does. A Draft loaded from the server overwrites all three from its
		// own columns, so this only ever seeds a brand-new form.
		picL1: props?.admin?.id ? String(props.admin.id) : null,
		picL2: null,
		picL3: null,
	};

	return {
		formOptions: {
			patientRecordSource,
			referenceAppointmentId,
			patientContactSource,
			coordinateSource,
			findTherapistsAllOfDay,
			patientId: props?.apptRef?.patient?.id,
			draftId: undefined,
			queueCode: undefined,
		},
		contactInformation,
		patientDetails,
		appointmentScheduling,
		additionalSettings,
	} as unknown as AppointmentBookingSchema;
};
